import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import {
  Banknote,
  CalendarClock,
  Landmark,
  ReceiptText,
  SlidersHorizontal,
  Undo2,
  BadgeCheck,
  BookOpenCheck,
  Building,
  Building2,
  CalendarDays,
  ClipboardList,
  FileSpreadsheet,
  GitPullRequestArrow,
  GraduationCap,
  Inbox,
  Library,
  Tags,
  FileSignature,
  FileCog,
  FileText,
  FolderCog,
  Handshake,
  Hash,
  History,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  Scale,
  Send,
  Settings2,
  ShieldCheck,
  Target,
  Truck,
  UserCog,
  Users,
  Wallet,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import { P } from '@/lib/permissions';
import { commissionRoutes } from '@/features/commission/routes';
import { CHANGE_REQUEST_READ, CONTRACT_READ, CP, ORDER_READ } from '@/api/commercial';
import { BOOKING_READ, PROJECT_READ, STAFF_READ } from '@/api/delivery';
import { CLASS_READ, STUDENT_READ, TP } from '@/api/training';
import HomeRoute from './HomeRoute';

/**
 * ONE registry for routes + sidebar navigation.
 *
 * To add a module page:
 *   1. API module in src/api/<domain>.ts
 *   2. Page component in src/features/<domain>/<Name>Page.tsx (default export)
 *   3. Add an entry below: path, `page: lazy(() => import(...))`, `perms` (any-of; "module.*" allowed) and,
 *      if it belongs in the sidebar, `nav: { group, label, icon }`.
 * The router guards the route with `perms` (PermissionDenied) and the sidebar hides items the user can't open.
 * Never use role names — only permission codes from /identity/me.
 */
export type NavGroupId =
  'overview' | 'sales' | 'approvals' | 'commercial' | 'operations' | 'training' | 'finance' | 'commission' | 'admin' | 'platform';

export const NAV_GROUPS: { id: NavGroupId; label: string }[] = [
  { id: 'overview', label: 'Tổng quan' },
  { id: 'sales', label: 'Kinh doanh' },
  { id: 'approvals', label: 'Phê duyệt' },
  { id: 'commercial', label: 'Hợp đồng & Đơn' },
  { id: 'operations', label: 'Vận hành' },
  { id: 'training', label: 'Đào tạo' },
  { id: 'finance', label: 'Tài chính' },
  { id: 'commission', label: 'Hoa hồng' },
  { id: 'admin', label: 'Quản trị' },
  { id: 'platform', label: 'Quản trị nền tảng' },
];

/** Groups shown to a Nguyên Bình platform admin without business permissions (see `isPlatformOnly`). */
export const PLATFORM_ONLY_GROUPS: readonly NavGroupId[] = ['platform'];

export interface NavMeta {
  group: NavGroupId;
  label: string;
  icon: LucideIcon;
  /** Sidebar link target if different from `path` (e.g. path has params). */
  to?: string;
  /** Exact match for active state. */
  end?: boolean;
}

export interface AppRoute {
  /** Path under the authenticated shell, no leading slash ("" = index). */
  path: string;
  page: LazyExoticComponent<ComponentType> | ComponentType;
  /** Any-of permission codes. Omit = any signed-in user. */
  perms?: readonly string[];
  nav?: NavMeta;
  /** Document title. */
  title: string;
}

const customerRead = [P.customerReadAll, P.customerReadOwn];
const quoteRead = [P.quoteReadOwn, P.quoteReadAll];
const approvalPolicyRead = ['approvals.policy.manage', 'approvals.policy.publish', P.approvalReadAll];

export const APP_ROUTES: AppRoute[] = [
  {
    path: '',
    title: 'Tổng quan',
    page: HomeRoute,
    nav: { group: 'overview', label: 'Tổng quan', icon: LayoutDashboard, end: true, to: '/' },
  },
  {
    // Header user menu → "Tài khoản của tôi" (any signed-in user: own avatar + profile).
    path: 'account',
    title: 'Tài khoản của tôi',
    page: lazy(() => import('@/features/account/AccountPage')),
  },

  // ---- Kinh doanh
  {
    path: 'customers',
    title: 'Khách hàng',
    perms: customerRead,
    page: lazy(() => import('@/features/customers/CustomerListPage')),
    nav: { group: 'sales', label: 'Khách hàng', icon: Building2 },
  },
  {
    path: 'customers/new',
    title: 'Tạo khách hàng',
    perms: [P.customerCreate],
    page: lazy(() => import('@/features/customers/CustomerCreatePage')),
  },
  {
    path: 'customers/:id',
    title: 'Chi tiết khách hàng',
    perms: customerRead,
    page: lazy(() => import('@/features/customers/CustomerDetailPage')),
  },
  {
    path: 'leads',
    title: 'Lead',
    perms: [P.leadManage],
    page: lazy(() => import('@/features/leads/LeadsPage')),
    nav: { group: 'sales', label: 'Lead', icon: Target },
  },
  {
    path: 'opportunities',
    title: 'Cơ hội',
    perms: [P.opportunityManage],
    page: lazy(() => import('@/features/opportunities/OpportunitiesPage')),
    nav: { group: 'sales', label: 'Cơ hội', icon: Handshake },
  },
  {
    path: 'quotes',
    title: 'Báo giá',
    perms: quoteRead,
    page: lazy(() => import('@/features/sales/QuotationListPage')),
    nav: { group: 'sales', label: 'Báo giá', icon: FileText, end: true },
  },
  {
    path: 'quotes/templates',
    title: 'Mẫu báo giá',
    perms: ['sales.quote.template.manage'],
    page: lazy(() => import('@/features/sales/TemplatesPage')),
    nav: { group: 'sales', label: 'Mẫu báo giá', icon: FileCog },
  },
  {
    path: 'quotes/:id',
    title: 'Báo giá',
    perms: quoteRead,
    page: lazy(() => import('@/features/sales/QuotationWorkspacePage')),
  },
  {
    path: 'classification-overrides',
    title: 'Phân loại chờ duyệt',
    perms: [P.classificationApprove],
    page: lazy(() => import('@/features/classification/PendingClassificationPage')),
    nav: { group: 'sales', label: 'Phân loại chờ duyệt', icon: Scale },
  },

  // ---- Modules in development (placeholders wired to permissions; replace `page` when the API lands)
  {
    path: 'approvals',
    title: 'Hộp phê duyệt',
    perms: [P.approvalStepApprove],
    page: lazy(() => import('@/features/approvals/InboxPage')),
    nav: { group: 'approvals', label: 'Hộp phê duyệt', icon: BadgeCheck, end: true },
  },
  {
    path: 'approvals/requests',
    title: 'Yêu cầu tôi đã gửi',
    // Anyone who can submit something for approval; the API returns only the caller's own requests.
    perms: ['approvals.*', 'sales.quote.submit_approval', 'commercial.*', 'finance.*', 'commission.*'],
    page: lazy(() => import('@/features/approvals/MyRequestsPage')),
    nav: { group: 'approvals', label: 'Yêu cầu tôi đã gửi', icon: Send, end: true },
  },
  {
    path: 'approvals/requests/:id',
    title: 'Yêu cầu duyệt',
    // Submitter, assignees, delegates and approvals.read.all may open it; the API answers 404 otherwise.
    page: lazy(() => import('@/features/approvals/RequestDetailPage')),
  },
  {
    path: 'approvals/all',
    title: 'Tất cả yêu cầu duyệt',
    perms: [P.approvalReadAll],
    page: lazy(() => import('@/features/approvals/AllRequestsPage')),
    nav: { group: 'approvals', label: 'Tất cả yêu cầu', icon: ListChecks },
  },
  {
    path: 'approvals/policies',
    title: 'Chính sách duyệt',
    perms: approvalPolicyRead,
    page: lazy(() => import('@/features/approvals/PoliciesPage')),
    nav: { group: 'approvals', label: 'Chính sách duyệt', icon: Workflow, end: true },
  },
  {
    path: 'approvals/policies/:id',
    title: 'Chính sách duyệt',
    perms: approvalPolicyRead,
    page: lazy(() => import('@/features/approvals/PolicyDetailPage')),
  },
  // ---- Hợp đồng & Đơn (features/commercial)
  {
    path: 'contracts',
    title: 'Hợp đồng',
    perms: CONTRACT_READ,
    page: lazy(() => import('@/features/commercial/ContractsPage')),
    nav: { group: 'commercial', label: 'Hợp đồng', icon: FileSignature },
  },
  {
    path: 'contracts/:id',
    title: 'Hợp đồng',
    perms: CONTRACT_READ,
    page: lazy(() => import('@/features/commercial/ContractDetailPage')),
  },
  {
    path: 'orders',
    title: 'Đơn hàng',
    perms: ORDER_READ,
    page: lazy(() => import('@/features/commercial/OrdersPage')),
    nav: { group: 'commercial', label: 'Đơn hàng', icon: ClipboardList },
  },
  {
    path: 'orders/new',
    title: 'Tạo đơn hàng',
    perms: [CP.orderWrite],
    page: lazy(() => import('@/features/commercial/OrderFormPage')),
  },
  {
    path: 'orders/:id',
    title: 'Đơn hàng',
    perms: ORDER_READ,
    page: lazy(() => import('@/features/commercial/OrderDetailPage')),
  },
  {
    path: 'orders/:id/edit',
    title: 'Sửa đơn hàng',
    perms: [CP.orderWrite],
    page: lazy(() => import('@/features/commercial/OrderFormPage')),
  },
  {
    path: 'change-requests',
    title: 'Yêu cầu thay đổi',
    perms: CHANGE_REQUEST_READ,
    page: lazy(() => import('@/features/commercial/ChangeRequestsPage')),
    nav: { group: 'commercial', label: 'Yêu cầu thay đổi', icon: GitPullRequestArrow },
  },
  {
    path: 'change-requests/new',
    title: 'Lập yêu cầu thay đổi',
    perms: [CP.changeRequestWrite],
    page: lazy(() => import('@/features/commercial/ChangeRequestEditorPage')),
  },
  {
    path: 'change-requests/:id',
    title: 'Yêu cầu thay đổi',
    perms: CHANGE_REQUEST_READ,
    page: lazy(() => import('@/features/commercial/ChangeRequestDetailPage')),
  },
  {
    path: 'change-requests/:id/edit',
    title: 'Sửa yêu cầu thay đổi',
    perms: [CP.changeRequestWrite],
    page: lazy(() => import('@/features/commercial/ChangeRequestEditorPage')),
  },
  {
    path: 'contract-types',
    title: 'Loại hợp đồng',
    perms: [CP.contractTypeManage],
    page: lazy(() => import('@/features/commercial/ContractTypesPage')),
    nav: { group: 'commercial', label: 'Loại hợp đồng', icon: Tags },
  },

  // ---- Vận hành (features/operations; handover endpoints are served by the Commercial module)
  {
    path: 'operations',
    title: 'Tiếp nhận bàn giao',
    perms: [CP.handoverProcess],
    page: lazy(() => import('@/features/operations/HandoverQueuePage')),
    nav: { group: 'operations', label: 'Tiếp nhận bàn giao', icon: Inbox, end: true },
  },
  {
    path: 'operations/handovers/:id',
    title: 'Bàn giao',
    perms: [CP.handoverProcess, CP.orderWrite],
    page: lazy(() => import('@/features/operations/HandoverDetailPage')),
  },
  {
    path: 'operations/projects',
    title: 'Dự án triển khai',
    perms: PROJECT_READ,
    page: lazy(() => import('@/features/operations/ProjectsPage')),
    nav: { group: 'operations', label: 'Dự án triển khai', icon: Truck },
  },
  {
    path: 'operations/projects/:id',
    title: 'Dự án triển khai',
    perms: PROJECT_READ,
    page: lazy(() => import('@/features/operations/ProjectDetailPage')),
  },
  {
    path: 'operations/tasks',
    title: 'Công việc',
    perms: PROJECT_READ,
    page: lazy(() => import('@/features/operations/WorkQueuePage')),
    nav: { group: 'operations', label: 'Công việc', icon: ListChecks },
  },
  {
    path: 'operations/calendar',
    title: 'Lịch tài nguyên',
    perms: BOOKING_READ,
    page: lazy(() => import('@/features/operations/ResourceCalendarPage')),
    nav: { group: 'operations', label: 'Lịch tài nguyên', icon: CalendarDays },
  },
  {
    path: 'operations/staff',
    title: 'Nhân sự triển khai',
    perms: STAFF_READ,
    page: lazy(() => import('@/features/operations/StaffPage')),
    nav: { group: 'operations', label: 'Nhân sự', icon: UserCog },
  },

  // ---- Đào tạo (features/training)
  {
    path: 'training',
    title: 'Lớp học',
    perms: CLASS_READ,
    page: lazy(() => import('@/features/training/ClassesPage')),
    nav: { group: 'training', label: 'Lớp học', icon: BookOpenCheck, end: true },
  },
  {
    path: 'training/classes/:id',
    title: 'Lớp học',
    perms: CLASS_READ,
    page: lazy(() => import('@/features/training/ClassDetailPage')),
  },
  {
    path: 'training/courses',
    title: 'Khóa học',
    perms: CLASS_READ,
    page: lazy(() => import('@/features/training/CoursesPage')),
    nav: { group: 'training', label: 'Khóa học', icon: Library },
  },
  {
    path: 'training/students',
    title: 'Học viên',
    perms: STUDENT_READ,
    page: lazy(() => import('@/features/training/StudentsPage')),
    nav: { group: 'training', label: 'Học viên', icon: GraduationCap },
  },
  {
    path: 'training/students/:id',
    title: 'Học viên',
    perms: STUDENT_READ,
    page: lazy(() => import('@/features/training/StudentDetailPage')),
  },
  {
    path: 'training/imports',
    title: 'Import học viên',
    perms: [TP.importRun],
    page: lazy(() => import('@/features/training/StudentImportPage')),
    nav: { group: 'training', label: 'Import học viên', icon: FileSpreadsheet },
  },
  // ---- Tài chính (Finance Desk). Perm codes mirror apps/api Modules/Finance FinancePerm.
  {
    path: 'finance',
    title: 'Bàn tài chính',
    perms: ['finance.receivable.read'],
    page: lazy(() => import('@/features/finance/FinanceDeskPage')),
    nav: { group: 'finance', label: 'Bàn tài chính', icon: Wallet, end: true },
  },
  {
    path: 'finance/receipts',
    title: 'Khoản thu',
    perms: ['finance.receivable.read', 'finance.receivable.read.own'],
    page: lazy(() => import('@/features/finance/ReceiptsPage')),
    nav: { group: 'finance', label: 'Khoản thu', icon: Banknote },
  },
  {
    path: 'finance/receipts/new',
    title: 'Ghi nhận khoản thu',
    perms: ['finance.receipt.create'],
    page: lazy(() => import('@/features/finance/ReceiptCreatePage')),
  },
  {
    path: 'finance/receipts/:id',
    title: 'Xác minh khoản thu',
    perms: ['finance.receivable.read', 'finance.receivable.read.own'],
    page: lazy(() => import('@/features/finance/ReceiptVerificationPage')),
  },
  {
    path: 'finance/receipts/:id/allocate',
    title: 'Phân bổ khoản thu',
    perms: ['finance.allocation.manage'],
    page: lazy(() => import('@/features/finance/AllocationPage')),
  },
  {
    path: 'finance/receivables',
    title: 'Công nợ phải thu',
    perms: ['finance.receivable.read', 'finance.receivable.read.own'],
    page: lazy(() => import('@/features/finance/ReceivablesPage')),
    nav: { group: 'finance', label: 'Công nợ', icon: ReceiptText },
  },
  {
    path: 'finance/receivables/:id',
    title: 'Chi tiết công nợ',
    perms: ['finance.receivable.read', 'finance.receivable.read.own'],
    page: lazy(() => import('@/features/finance/ReceivableDetailPage')),
  },
  {
    path: 'finance/contracts',
    title: 'Hợp đồng – tài chính',
    perms: ['finance.receivable.read', 'finance.receivable.read.own', 'finance.schedule.read'],
    page: lazy(() => import('@/features/finance/ContractLookupPage')),
    nav: { group: 'finance', label: 'Hợp đồng & lịch thu', icon: CalendarClock },
  },
  {
    path: 'finance/contracts/:id',
    title: 'Tài chính hợp đồng',
    perms: ['finance.receivable.read', 'finance.receivable.read.own', 'finance.schedule.read'],
    page: lazy(() => import('@/features/finance/ContractFinancePage')),
  },
  {
    path: 'finance/contracts/:id/schedules/new',
    title: 'Lập lịch thu',
    perms: ['finance.schedule.manage'],
    page: lazy(() => import('@/features/finance/ScheduleEditorPage')),
  },
  {
    path: 'finance/schedules/:scheduleId',
    title: 'Lịch thu',
    perms: ['finance.schedule.read'],
    page: lazy(() => import('@/features/finance/ScheduleEditorPage')),
  },
  {
    path: 'finance/invoices',
    title: 'Hóa đơn',
    perms: ['finance.invoice.manage', 'finance.receivable.read'],
    page: lazy(() => import('@/features/finance/InvoicesPage')),
    nav: { group: 'finance', label: 'Hóa đơn', icon: FileText },
  },
  {
    path: 'finance/reconciliation',
    title: 'Đối soát sao kê',
    perms: ['finance.reconciliation.manage'],
    page: lazy(() => import('@/features/finance/ReconciliationPage')),
    nav: { group: 'finance', label: 'Đối soát', icon: Landmark },
  },
  {
    path: 'finance/reconciliation/:id',
    title: 'Lô đối soát',
    perms: ['finance.reconciliation.manage'],
    page: lazy(() => import('@/features/finance/ReconciliationDetailPage')),
  },
  {
    path: 'finance/refunds',
    title: 'Hoàn tiền',
    perms: ['finance.refund.request', 'finance.refund.pay', 'finance.receivable.read'],
    page: lazy(() => import('@/features/finance/RefundsPage')),
    nav: { group: 'finance', label: 'Hoàn tiền', icon: Undo2 },
  },
  {
    path: 'finance/refunds/:id',
    title: 'Yêu cầu hoàn tiền',
    perms: ['finance.refund.request', 'finance.refund.pay', 'finance.receivable.read'],
    page: lazy(() => import('@/features/finance/RefundDetailPage')),
  },
  {
    path: 'finance/adjustments',
    title: 'Điều chỉnh tài chính',
    perms: ['finance.adjustment.request', 'finance.receivable.read'],
    page: lazy(() => import('@/features/finance/AdjustmentsPage')),
    nav: { group: 'finance', label: 'Điều chỉnh', icon: SlidersHorizontal },
  },
  // Hoa hồng — real routes live in features/commission/routes.ts
  ...commissionRoutes,

  // ---- Quản trị
  {
    path: 'admin/users',
    title: 'Người dùng',
    perms: [P.usersManage],
    page: lazy(() => import('@/features/admin/UsersPage')),
    nav: { group: 'admin', label: 'Người dùng', icon: Users },
  },
  {
    path: 'admin/roles',
    title: 'Vai trò & quyền',
    perms: [P.rolesManage],
    page: lazy(() => import('@/features/admin/RolesPage')),
    nav: { group: 'admin', label: 'Vai trò & quyền', icon: ShieldCheck },
  },
  {
    path: 'admin/teams',
    title: 'Nhóm',
    perms: [P.usersManage],
    page: lazy(() => import('@/features/admin/TeamsPage')),
    nav: { group: 'admin', label: 'Nhóm', icon: UserCog },
  },
  {
    path: 'admin/delegations',
    title: 'Ủy quyền duyệt',
    perms: [P.delegationsOwn, P.delegationsManage],
    page: lazy(() => import('@/features/admin/DelegationsPage')),
    nav: { group: 'admin', label: 'Ủy quyền duyệt', icon: KeyRound },
  },
  {
    path: 'admin/catalog',
    title: 'Danh mục',
    perms: [P.catalogManage],
    page: lazy(() => import('@/features/admin/CatalogPage')),
    nav: { group: 'admin', label: 'Danh mục', icon: FolderCog },
  },
  {
    path: 'admin/settings',
    title: 'Cấu hình',
    perms: [P.settingsManage],
    page: lazy(() => import('@/features/admin/SettingsPage')),
    nav: { group: 'admin', label: 'Cấu hình', icon: Settings2 },
  },
  {
    path: 'admin/number-series',
    title: 'Dãy số',
    perms: [P.settingsManage],
    page: lazy(() => import('@/features/admin/NumberSeriesPage')),
    nav: { group: 'admin', label: 'Dãy số', icon: Hash },
  },
  {
    path: 'audit',
    title: 'Nhật ký hệ thống',
    perms: [P.auditRead, P.auditFinancialRead],
    page: lazy(() => import('@/features/audit/AuditExplorerPage')),
    nav: { group: 'admin', label: 'Nhật ký hệ thống', icon: History },
  },

  // ---- Quản trị nền tảng (Nguyên Bình platform admins only; permission never granted by tenant roles)
  {
    path: 'platform/tenants',
    title: 'Doanh nghiệp',
    perms: [P.platformTenantsManage],
    page: lazy(() => import('@/features/platform-admin/TenantsPage')),
    nav: { group: 'platform', label: 'Doanh nghiệp', icon: Building },
  },
  {
    path: 'platform/tenants/:id',
    title: 'Chi tiết doanh nghiệp',
    perms: [P.platformTenantsManage],
    page: lazy(() => import('@/features/platform-admin/TenantDetailPage')),
  },
];
